import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaEdit, FaTrash, FaEye, FaUser, FaClock, FaArrowsAlt, FaCalendarAlt, FaHourglass, FaFile } from 'react-icons/fa';
import calendarTaskService from '../../services/calendarTaskService';
import { notify } from '../CustomToast';
import CreateTaskModal from './CreateTaskModal';
import CalendarTaskDocumentsModal from './CalendarTaskDocumentsModal';
import CalendarDeleteTaskModal from './CalendarDeleteTaskModal';
import caretDoubleUpIcon from '../../assets/CaretDoubleUp.svg';
import calendarDotsIcon from '../../assets/CalendarDots.svg';
import arrowRightIcon from '../../assets/ArrowRight.svg';
import fileTextIcon from '../../assets/FileText.svg';
import hourglassLowIcon from '../../assets/HourglassLow.svg';
import '../Projects/ProjectComposer.css';
import './TaskDetailsModal.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const STATUS_OPTIONS = [
    { value: 'confirmed',  label: 'CONFIRMED' },
    { value: 'tentative',  label: 'TENTATIVE' },
    { value: 'cancelled',  label: 'CANCELLED' },
    { value: 'pending',    label: 'PENDING' },
    { value: 'scheduled',  label: 'SCHEDULED' },
];

const getStatusBadgeClass = (task) => {
    if (task.is_completed) return 'tdm-badge tdm-badge-completed';
    const s = (task.status || '').toLowerCase();
    if (s === 'confirmed')  return 'tdm-badge tdm-badge-confirmed';
    if (s === 'tentative')  return 'tdm-badge tdm-badge-tentative';
    if (s === 'cancelled')  return 'tdm-badge tdm-badge-cancelled';
    if (s === 'pending')    return 'tdm-badge tdm-badge-pending';
    if (s === 'scheduled')  return 'tdm-badge tdm-badge-scheduled';
    return 'tdm-badge tdm-badge-confirmed';
};

const getStatusLabel = (task) => {
    if (task.is_completed) return 'COMPLETED';
    return (task.status || 'confirmed').toUpperCase();
};

const PRIORITY_OPTIONS = [
    { value: 'low',      label: 'Low' },
    { value: 'normal',   label: 'Normal' },
    { value: 'high',     label: 'High' },
    { value: 'critical', label: 'Critical' },
];

const getPriorityBadgeClass = (priority) => {
    const p = (priority || 'normal').toLowerCase();
    if (p === 'critical') return 'tdm-badge tdm-badge-priority-critical';
    if (p === 'high') return 'tdm-badge tdm-badge-priority-high';
    if (p === 'low') return 'tdm-badge tdm-badge-priority-low';
    return 'tdm-badge tdm-badge-priority-normal';
};

const isRecurring = (task) =>
    task && (task.parent_id !== null && task.parent_id !== undefined || task.recurrence_rule);

// ─── Component ─────────────────────────────────────────────────────────────────

const TaskDetailsModal = ({
    isOpen,
    task,
    onClose,
    onUpdated,
    onDeleted,
    currentUserId,
}) => {
    const [isCompleted, setIsCompleted] = useState(task?.is_completed ?? false);
    const [taskStatus, setTaskStatus] = useState(task?.status ?? 'confirmed');
    const [isToggling, setIsToggling] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [fullTask, setFullTask] = useState(null);
    const [isLoadingTask, setIsLoadingTask] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const statusDropdownRef = useRef(null);
    const [taskPriority, setTaskPriority] = useState(task?.priority ?? 'normal');
    const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
    const priorityDropdownRef = useRef(null);

    // For recurring tasks — which scope to act on
    const [pendingAction, setPendingAction] = useState(null); // 'edit' | 'delete'
    const [showRecurringChoice, setShowRecurringChoice] = useState(false);

    useEffect(() => {
        setIsCompleted(task?.is_completed ?? false);
        setTaskStatus(task?.status ?? 'confirmed');
        setTaskPriority(task?.priority ?? 'normal');
    }, [task?.id, task?.is_completed, task?.status, task?.priority]);

    useEffect(() => {
        if (!showStatusDropdown) return;
        const handler = (e) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showStatusDropdown]);

    useEffect(() => {
        if (!showPriorityDropdown) return;
        const handler = (e) => {
            if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(e.target)) {
                setShowPriorityDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showPriorityDropdown]);

    useEffect(() => {
        if (!isOpen || !task?.id) { setFullTask(null); setIsLoadingTask(false); return; }
        setIsLoadingTask(true);
        setFullTask(null);
        calendarTaskService.getTask(task.id)
            .then(res => {
                const data = res?.data ?? res;
                setFullTask(data);
            })
            .catch(() => setFullTask(null))
            .finally(() => setIsLoadingTask(false));
    }, [isOpen, task?.id]);

    if (!isOpen || !task) return null;

    const isCreator = String(currentUserId) === String(task.creator_id ?? task.creator?.id ?? task.user_id);
    const recurring = isRecurring(task);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    // ── Toggle complete ────────────────────────────────────────────────────────
    const handleToggleComplete = async () => {
        setIsToggling(true);
        try {
            const res = await calendarTaskService.toggleComplete(task.id);
            const updated = res?.data ?? res;
            const nowCompleted = !isCompleted;
            setIsCompleted(nowCompleted);
            if (onUpdated) onUpdated(updated);
            if (nowCompleted) onClose();
        } catch (err) {
            notify('error', err?.message || 'Failed to update task');
        } finally {
            setIsToggling(false);
        }
    };

    // ── Status change ─────────────────────────────────────────────────────────
    const handleStatusChange = async (newStatus) => {
        setShowStatusDropdown(false);
        const prev = taskStatus;
        setTaskStatus(newStatus);
        try {
            const res = await calendarTaskService.updateTask(task.id, { status: newStatus });
            const updated = res?.data ?? res;
            if (onUpdated) onUpdated(updated);
        } catch (err) {
            setTaskStatus(prev);
            notify('error', err?.message || 'Failed to update status');
        }
    };

    // ── Priority change ────────────────────────────────────────────────────────
    const handlePriorityChange = async (newPriority) => {
        setShowPriorityDropdown(false);
        const prev = taskPriority;
        setTaskPriority(newPriority);
        try {
            const res = await calendarTaskService.updateTask(task.id, { priority: newPriority });
            const updated = res?.data ?? res;
            if (onUpdated) onUpdated(updated);
        } catch (err) {
            setTaskPriority(prev);
            notify('error', err?.message || 'Failed to update priority');
        }
    };

    // ── Edit ───────────────────────────────────────────────────────────────────
    // Merge fullTask (has assignees/creator from API) over the grid task prop
    const taskWithFullData = fullTask ? { ...task, ...fullTask } : task;

    const handleEditClick = () => {
        if (recurring) {
            setPendingAction('edit');
            setShowRecurringChoice(true);
        } else {
            setEditTask(taskWithFullData);
            setShowEditModal(true);
        }
    };

    const handleRecurringChoice = (scope) => {
        setShowRecurringChoice(false);
        if (pendingAction === 'edit') {
            if (scope === 'this') {
                setEditTask(taskWithFullData);
            } else {
                // All occurrences — use the parent task data
                setEditTask({ ...taskWithFullData, id: task.parent_id ?? task.id });
            }
            setShowEditModal(true);
        } else if (pendingAction === 'delete') {
            const idToDelete = scope === 'this' ? task.id : (task.parent_id ?? task.id);
            executeDelete(idToDelete);
        }
        setPendingAction(null);
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDeleteClick = () => {
        if (recurring) {
            setPendingAction('delete');
            setShowRecurringChoice(true);
        } else {
            setShowDeleteConfirm(true);
        }
    };

    const executeDelete = async (id) => {
        setIsDeleting(true);
        try {
            await calendarTaskService.deleteTask(id);
            notify('success', 'Task deleted successfully');
            onClose();
            if (onDeleted) onDeleted();
        } catch (err) {
            notify('error', err?.message || 'Failed to delete task');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleConfirmDelete = () => {
        setShowDeleteConfirm(false);
        executeDelete(task.id);
    };

    // ── Attachments ────────────────────────────────────────────────────────────
    // UploadedDocumentsModal expects attachment.upload.{original_filename, file_url, ...}
    // Calendar task returns UploadResource fields directly on the object — normalize here
    const rawAttachments = fullTask?.attachments || task.attachments || [];
    const attachments = rawAttachments.map(a => ({
        ...a,
        upload: a.upload ?? {
            original_filename: a.original_filename,
            file_url: a.file_url,
            mime_type: a.mime_type,
            extension: a.extension,
        },
    }));

    // ── Expected days ──────────────────────────────────────────────────────────
    const expectedDays = task.expected_days ?? task.duration_days ?? null;

    return (
        <>
            {/* Edit modal */}
            {showEditModal && (
                <CreateTaskModal
                    isOpen={showEditModal}
                    task={editTask}
                    onClose={() => { setShowEditModal(false); setEditTask(null); }}
                    onSuccess={(updated) => {
                        setShowEditModal(false);
                        setEditTask(null);
                        if (onUpdated) onUpdated(updated);
                        onClose();
                    }}
                />
            )}

            {ReactDOM.createPortal(
                <div className="project-composer-overlay tdm-overlay" onClick={handleBackdropClick} style={(showDocumentsModal || showEditModal || showDeleteConfirm) ? { display: 'none' } : undefined}>
                    <div
                        className="project-composer-modal tdm-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ── Header ── */}
                        <div className="project-composer-header tdm-header">
                            <div className="tdm-header-left">
                                <h2 className="tdm-header-title">Task Details</h2>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 16C13.25 16 14.3125 15.5625 15.1875 14.6875C16.0625 13.8125 16.5 12.75 16.5 11.5C16.5 10.25 16.0625 9.1875 15.1875 8.3125C14.3125 7.4375 13.25 7 12 7C10.75 7 9.6875 7.4375 8.8125 8.3125C7.9375 9.1875 7.5 10.25 7.5 11.5C7.5 12.75 7.9375 13.8125 8.8125 14.6875C9.6875 15.5625 10.75 16 12 16ZM12 14.2C11.25 14.2 10.6125 13.9375 10.0875 13.4125C9.5625 12.8875 9.3 12.25 9.3 11.5C9.3 10.75 9.5625 10.1125 10.0875 9.5875C10.6125 9.0625 11.25 8.8 12 8.8C12.75 8.8 13.3875 9.0625 13.9125 9.5875C14.4375 10.1125 14.7 10.75 14.7 11.5C14.7 12.25 14.4375 12.8875 13.9125 13.4125C13.3875 13.9375 12.75 14.2 12 14.2ZM12 19C9.56667 19 7.35 18.3208 5.35 16.9625C3.35 15.6042 1.9 13.7833 1 11.5C1.9 9.21667 3.35 7.39583 5.35 6.0375C7.35 4.67917 9.56667 4 12 4C14.4333 4 16.65 4.67917 18.65 6.0375C20.65 7.39583 22.1 9.21667 23 11.5C22.1 13.7833 20.65 15.6042 18.65 16.9625C16.65 18.3208 14.4333 19 12 19Z" fill="white"/>
                                </svg>
                            </div>
                            <div className="tdm-header-actions">
                                {isCreator && (
                                    <>
                                        <button className="tdm-action-btn" onClick={handleEditClick} type="button">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g clipPath="url(#clip_edit)">
                                                    <path d="M14.1166 4.54029C14.4691 4.1879 14.6671 3.70993 14.6672 3.21152C14.6673 2.71311 14.4693 2.23509 14.1169 1.88262C13.7646 1.53015 13.2866 1.33209 12.7882 1.33203C12.2898 1.33197 11.8117 1.5299 11.4593 1.88229L2.56194 10.7816C2.40715 10.936 2.29268 11.126 2.22861 11.335L1.34794 14.2363C1.33071 14.2939 1.32941 14.3552 1.34417 14.4135C1.35894 14.4719 1.38922 14.5251 1.4318 14.5676C1.47439 14.6101 1.52769 14.6403 1.58605 14.655C1.6444 14.6697 1.70565 14.6683 1.76327 14.651L4.66527 13.771C4.87405 13.7075 5.06406 13.5937 5.21861 13.4396L14.1166 4.54029Z" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 3.33203L12.6667 5.9987" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                </g>
                                                <defs><clipPath id="clip_edit"><rect width="16" height="16" fill="white"/></clipPath></defs>
                                            </svg>
                                            <span className="tdm-action-btn-label">Edit</span>
                                        </button>
                                        <button className="tdm-action-btn" onClick={handleDeleteClick} type="button" disabled={isDeleting}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12.6673 4V13.3333C12.6673 13.687 12.5268 14.0261 12.2768 14.2761C12.0267 14.5262 11.6876 14.6667 11.334 14.6667H4.66732C4.3137 14.6667 3.97456 14.5262 3.72451 14.2761C3.47446 14.0261 3.33398 13.687 3.33398 13.3333V4" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M2 4H14" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M5.33398 3.9987V2.66536C5.33398 2.31174 5.47446 1.9726 5.72451 1.72256C5.97456 1.47251 6.3137 1.33203 6.66732 1.33203H9.33398C9.68761 1.33203 10.0267 1.47251 10.2768 1.72256C10.5268 1.9726 10.6673 2.31174 10.6673 2.66536V3.9987" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="tdm-action-btn-label">Delete</span>
                                        </button>
                                    </>
                                )}
                                <button className="tdm-header-close-btn" onClick={onClose} type="button">
                                    <FaTimes size={16} />
                                </button>
                            </div>
                        </div>

                        {/* ── Body ── */}
                        <div className="project-composer-body tdm-body">
                        {isLoadingTask ? (
                            <div className="tdm-skeleton-body">
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-value-wide" />
                                </div>
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-value-mid" />
                                </div>
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-value-mid" />
                                </div>
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-badge" />
                                </div>
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-badge" />
                                </div>
                                <div className="tdm-skeleton-row">
                                    <div className="tdm-skeleton tdm-skeleton-label" />
                                    <div className="tdm-skeleton tdm-skeleton-value-wide" />
                                </div>
                                <div className="tdm-skeleton tdm-skeleton-desc" />
                            </div>
                        ) : (<>

                            {/* Recurring choice */}
                            {showRecurringChoice && (
                                <div className="tdm-recurring-choice">
                                    <p className="tdm-recurring-choice-title">
                                        {pendingAction === 'edit' ? 'Edit which occurrences?' : 'Delete which occurrences?'}
                                    </p>
                                    <div className="tdm-recurring-choice-btns">
                                        <button
                                            type="button"
                                            className="tdm-btn-occurrence"
                                            onClick={() => handleRecurringChoice('this')}
                                        >
                                            This occurrence
                                        </button>
                                        <button
                                            type="button"
                                            className="tdm-btn-occurrence"
                                            onClick={() => handleRecurringChoice('all')}
                                        >
                                            All occurrences
                                        </button>
                                        <button
                                            type="button"
                                            className="tdm-btn-occurrence-cancel"
                                            onClick={() => { setShowRecurringChoice(false); setPendingAction(null); }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 1. Task Name */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label tdm-label-task-name">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.5003 10.0003C17.5003 10.1661 17.4345 10.3251 17.3173 10.4423C17.2001 10.5595 17.0411 10.6253 16.8753 10.6253H10.0003C9.83459 10.6253 9.67562 10.5595 9.55841 10.4423C9.4412 10.3251 9.37535 10.1661 9.37535 10.0003C9.37535 9.83459 9.4412 9.67562 9.55841 9.55841C9.67562 9.4412 9.83459 9.37535 10.0003 9.37535H16.8753C17.0411 9.37535 17.2001 9.4412 17.3173 9.55841C17.4345 9.67562 17.5003 9.83459 17.5003 10.0003ZM10.0003 5.62535H16.8753C17.0411 5.62535 17.2001 5.5595 17.3173 5.44229C17.4345 5.32508 17.5003 5.16611 17.5003 5.00035C17.5003 4.83459 17.4345 4.67562 17.3173 4.55841C17.2001 4.4412 17.0411 4.37535 16.8753 4.37535H10.0003C9.83459 4.37535 9.67562 4.4412 9.55841 4.55841C9.4412 4.67562 9.37535 4.83459 9.37535 5.00035C9.37535 5.16611 9.4412 5.32508 9.55841 5.44229C9.67562 5.5595 9.83459 5.62535 10.0003 5.62535ZM16.8753 14.3753H10.0003C9.83459 14.3753 9.67562 14.4412 9.55841 14.5584C9.4412 14.6756 9.37535 14.8346 9.37535 15.0003C9.37535 15.1661 9.4412 15.3251 9.55841 15.4423C9.67562 15.5595 9.83459 15.6253 10.0003 15.6253H16.8753C17.0411 15.6253 17.2001 15.5595 17.3173 15.4423C17.4345 15.3251 17.5003 15.1661 17.5003 15.0003C17.5003 14.8346 17.4345 14.6756 17.3173 14.5584C17.2001 14.4412 17.0411 14.3753 16.8753 14.3753ZM6.43316 3.30816L4.37535 5.36675L3.56753 4.55816C3.45026 4.44088 3.2912 4.375 3.12535 4.375C2.9595 4.375 2.80044 4.44088 2.68316 4.55816C2.56588 4.67544 2.5 4.8345 2.5 5.00035C2.5 5.1662 2.56588 5.32526 2.68316 5.44254L3.93316 6.69254C3.99121 6.75065 4.06014 6.79674 4.13601 6.8282C4.21188 6.85965 4.29321 6.87584 4.37535 6.87584C4.45748 6.87584 4.53881 6.85965 4.61469 6.8282C4.69056 6.79674 4.75949 6.75065 4.81753 6.69254L7.31754 4.19253C7.43481 4.07526 7.5007 3.9162 7.5007 3.75035C7.50069 3.5845 7.43481 3.42544 7.31754 3.30816C7.20026 3.19088 7.0412 3.125 6.87535 3.125C6.7095 3.125 6.55044 3.19088 6.43316 3.30816ZM6.43316 8.30816L4.37535 10.3668L3.56753 9.55816C3.45026 9.44088 3.2912 9.375 3.12535 9.375C2.9595 9.375 2.80044 9.44088 2.68316 9.55816C2.56588 9.67544 2.5 9.8345 2.5 10.0003C2.5 10.0825 2.51618 10.1638 2.5476 10.2397C2.57903 10.3155 2.62509 10.3845 2.68316 10.4425L3.93316 11.6925C3.99121 11.7506 4.06014 11.7967 4.13601 11.8282C4.21188 11.8597 4.29321 11.8758 4.37535 11.8758C4.45748 11.8758 4.53881 11.8597 4.61469 11.8282C4.69056 11.7967 4.75949 11.7506 4.81753 11.6925L7.31754 9.19254C7.43481 9.07526 7.50069 8.9162 7.5007 8.75035C7.50069 8.5845 7.43481 8.42544 7.31754 8.30816C7.20026 8.19088 7.0412 8.125 6.87535 8.125C6.7095 8.125 6.55044 8.19088 6.43316 8.30816ZM6.43316 13.3082L4.37535 15.3668L3.56753 14.5582C3.50947 14.5001 3.44053 14.454 3.36466 14.4226C3.28879 14.3912 3.20747 14.375 3.12535 14.375C3.04323 14.375 2.96191 14.3912 2.88604 14.4226C2.81017 14.454 2.74123 14.5001 2.68316 14.5582C2.62509 14.6162 2.57903 14.6852 2.5476 14.761C2.51618 14.8369 2.5 14.9182 2.5 15.0003C2.5 15.0825 2.51618 15.1638 2.5476 15.2397C2.57903 15.3155 2.62509 15.3845 2.68316 15.4425L3.93316 16.6925C3.99121 16.7506 4.06014 16.7967 4.13601 16.8282C4.21188 16.8597 4.29321 16.8758 4.37535 16.8758C4.45748 16.8758 4.53881 16.8597 4.61469 16.8282C4.69056 16.7967 4.75949 16.7506 4.81753 16.6925L7.31754 14.1925C7.43481 14.0753 7.5007 13.9162 7.5007 13.7503C7.50069 13.5845 7.43481 13.4254 7.31754 13.3082C7.20026 13.1909 7.0412 13.125 6.87535 13.125C6.7095 13.125 6.55044 13.1909 6.43316 13.3082Z" fill="#343330" fillOpacity="0.85"/>
                                    </svg>
                                    Task Name
                                </span>
                                <span className="tdm-detail-value tdm-task-title">{task.title}</span>
                            </div>

                            {/* 2. Description */}
                            {task.description && (
                                <div className="tdm-description-box">
                                    {task.description}
                                </div>
                            )}

                            {/* Divider */}
                            <div className="tdm-divider" />

                            {/* 3. Created by */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.4329 13.2517C13.481 11.6061 12.0142 10.4261 10.3023 9.8667C11.1491 9.36261 11.8069 8.5945 12.1749 7.68032C12.5429 6.76614 12.6006 5.75645 12.3392 4.8063C12.0778 3.85615 11.5117 3.01807 10.7279 2.42078C9.94408 1.82349 8.98587 1.5 8.00041 1.5C7.01496 1.5 6.05674 1.82349 5.27293 2.42078C4.48911 3.01807 3.92304 3.85615 3.66163 4.8063C3.40022 5.75645 3.45793 6.76614 3.82591 7.68032C4.19388 8.5945 4.85177 9.36261 5.69854 9.8667C3.98666 10.4255 2.51979 11.6055 1.56791 13.2517C1.53301 13.3086 1.50985 13.372 1.49982 13.438C1.48978 13.504 1.49307 13.5713 1.50949 13.636C1.52591 13.7008 1.55512 13.7615 1.59541 13.8148C1.63569 13.868 1.68624 13.9127 1.74405 13.9461C1.80187 13.9795 1.86579 14.0009 1.93204 14.0092C1.9983 14.0175 2.06554 14.0125 2.1298 13.9943C2.19407 13.9762 2.25405 13.9454 2.30622 13.9038C2.35838 13.8621 2.40168 13.8104 2.43354 13.7517C3.61104 11.7167 5.69229 10.5017 8.00041 10.5017C10.3085 10.5017 12.3898 11.7167 13.5673 13.7517C13.5992 13.8104 13.6424 13.8621 13.6946 13.9038C13.7468 13.9454 13.8068 13.9762 13.871 13.9943C13.9353 14.0125 14.0025 14.0175 14.0688 14.0092C14.135 14.0009 14.199 13.9795 14.2568 13.9461C14.3146 13.9127 14.3651 13.868 14.4054 13.8148C14.4457 13.7615 14.4749 13.7008 14.4913 13.636C14.5078 13.5713 14.511 13.504 14.501 13.438C14.491 13.372 14.4678 13.3086 14.4329 13.2517ZM4.50041 6.0017C4.50041 5.30947 4.70568 4.63278 5.09027 4.0572C5.47485 3.48163 6.02148 3.03303 6.66102 2.76812C7.30056 2.50322 8.0043 2.4339 8.68323 2.56895C9.36216 2.704 9.9858 3.03734 10.4753 3.52683C10.9648 4.01631 11.2981 4.63995 11.4332 5.31888C11.5682 5.99782 11.4989 6.70155 11.234 7.34109C10.9691 7.98063 10.5205 8.52726 9.94491 8.91184C9.36934 9.29643 8.69265 9.5017 8.00041 9.5017C7.07246 9.50071 6.1828 9.13164 5.52664 8.47548C4.87047 7.81932 4.50141 6.92965 4.50041 6.0017Z" fill="#343330" fillOpacity="0.85"/>
                                    </svg>
                                    Created by
                                </span>
                                <span className="tdm-detail-value">
                                    {fullTask?.creator?.name || fullTask?.creator?.email || task.creator?.name || '—'}
                                </span>
                            </div>

                            {/* 4. Team Member */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.4329 13.2517C13.481 11.6061 12.0142 10.4261 10.3023 9.8667C11.1491 9.36261 11.8069 8.5945 12.1749 7.68032C12.5429 6.76614 12.6006 5.75645 12.3392 4.8063C12.0778 3.85615 11.5117 3.01807 10.7279 2.42078C9.94408 1.82349 8.98587 1.5 8.00041 1.5C7.01496 1.5 6.05674 1.82349 5.27293 2.42078C4.48911 3.01807 3.92304 3.85615 3.66163 4.8063C3.40022 5.75645 3.45793 6.76614 3.82591 7.68032C4.19388 8.5945 4.85177 9.36261 5.69854 9.8667C3.98666 10.4255 2.51979 11.6055 1.56791 13.2517C1.53301 13.3086 1.50985 13.372 1.49982 13.438C1.48978 13.504 1.49307 13.5713 1.50949 13.636C1.52591 13.7008 1.55512 13.7615 1.59541 13.8148C1.63569 13.868 1.68624 13.9127 1.74405 13.9461C1.80187 13.9795 1.86579 14.0009 1.93204 14.0092C1.9983 14.0175 2.06554 14.0125 2.1298 13.9943C2.19407 13.9762 2.25405 13.9454 2.30622 13.9038C2.35838 13.8621 2.40168 13.8104 2.43354 13.7517C3.61104 11.7167 5.69229 10.5017 8.00041 10.5017C10.3085 10.5017 12.3898 11.7167 13.5673 13.7517C13.5992 13.8104 13.6424 13.8621 13.6946 13.9038C13.7468 13.9454 13.8068 13.9762 13.871 13.9943C13.9353 14.0125 14.0025 14.0175 14.0688 14.0092C14.135 14.0009 14.199 13.9795 14.2568 13.9461C14.3146 13.9127 14.3651 13.868 14.4054 13.8148C14.4457 13.7615 14.4749 13.7008 14.4913 13.636C14.5078 13.5713 14.511 13.504 14.501 13.438C14.491 13.372 14.4678 13.3086 14.4329 13.2517ZM4.50041 6.0017C4.50041 5.30947 4.70568 4.63278 5.09027 4.0572C5.47485 3.48163 6.02148 3.03303 6.66102 2.76812C7.30056 2.50322 8.0043 2.4339 8.68323 2.56895C9.36216 2.704 9.9858 3.03734 10.4753 3.52683C10.9648 4.01631 11.2981 4.63995 11.4332 5.31888C11.5682 5.99782 11.4989 6.70155 11.234 7.34109C10.9691 7.98063 10.5205 8.52726 9.94491 8.91184C9.36934 9.29643 8.69265 9.5017 8.00041 9.5017C7.07246 9.50071 6.1828 9.13164 5.52664 8.47548C4.87047 7.81932 4.50141 6.92965 4.50041 6.0017Z" fill="#343330" fillOpacity="0.85"/>
                                    </svg>
                                    Team Member
                                </span>
                                <span className="tdm-detail-value">
                                    {(fullTask?.assignees || []).length > 0
                                        ? (fullTask.assignees).map(a => a.name || a.email).join(', ')
                                        : '—'}
                                </span>
                            </div>

                            {/* 5. Status */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM8 13.5C6.91221 13.5 5.84884 13.1774 4.94437 12.5731C4.0399 11.9687 3.33495 11.1098 2.91867 10.1048C2.50238 9.09977 2.39347 7.9939 2.60568 6.927C2.8179 5.86011 3.34173 4.8801 4.11092 4.11091C4.8801 3.34172 5.86011 2.8179 6.92701 2.60568C7.9939 2.39346 9.09977 2.50238 10.1048 2.91866C11.1098 3.33494 11.9687 4.03989 12.5731 4.94436C13.1774 5.84883 13.5 6.9122 13.5 8C13.4983 9.45818 12.9184 10.8562 11.8873 11.8873C10.8562 12.9184 9.45819 13.4983 8 13.5ZM12 8C12 8.13261 11.9473 8.25979 11.8536 8.35355C11.7598 8.44732 11.6326 8.5 11.5 8.5H8C7.86739 8.5 7.74022 8.44732 7.64645 8.35355C7.55268 8.25979 7.5 8.13261 7.5 8V4.5C7.5 4.36739 7.55268 4.24021 7.64645 4.14645C7.74022 4.05268 7.86739 4 8 4C8.13261 4 8.25979 4.05268 8.35356 4.14645C8.44732 4.24021 8.5 4.36739 8.5 4.5V7.5H11.5C11.6326 7.5 11.7598 7.55268 11.8536 7.64645C11.9473 7.74021 12 7.86739 12 8Z" fill="#343330" fillOpacity="0.85"/>
                                    </svg>
                                    Status
                                </span>
                                <span className="tdm-detail-value">
                                    <div className="tdm-status-dropdown-container" ref={statusDropdownRef}>
                                        <span
                                            className={`${getStatusBadgeClass({ status: taskStatus, is_completed: isCompleted })} tdm-status-badge-btn ${isCreator ? 'tdm-status-clickable' : ''}`}
                                            onClick={isCreator && !isCompleted ? () => setShowStatusDropdown(p => !p) : undefined}
                                        >
                                            {getStatusLabel({ status: taskStatus, is_completed: isCompleted })}
                                            {isCreator && !isCompleted && (
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M4 6L8 10L12 6" stroke="white" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </span>
                                        {showStatusDropdown && (
                                            <div className="tdm-status-dropdown-menu">
                                                {STATUS_OPTIONS.map(opt => (
                                                    <div
                                                        key={opt.value}
                                                        className={`tdm-status-dropdown-item ${taskStatus === opt.value ? 'active' : ''}`}
                                                        onClick={(e) => { e.stopPropagation(); handleStatusChange(opt.value); }}
                                                    >
                                                        <span className={getStatusBadgeClass({ status: opt.value, is_completed: false })}>
                                                            {opt.label}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </span>
                            </div>

                            {/* 6. Priority */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label">
                                    <img src={caretDoubleUpIcon} alt="" className="tdm-row-icon" style={{width:14,height:14}} />
                                    Priority
                                </span>
                                <span className="tdm-detail-value tdm-priority-value">
                                    <div className="tdm-status-dropdown-container" ref={priorityDropdownRef}>
                                        <span
                                            className={`${getPriorityBadgeClass(taskPriority)} tdm-status-badge-btn ${isCreator ? 'tdm-status-clickable' : ''}`}
                                            onClick={isCreator ? () => setShowPriorityDropdown(p => !p) : undefined}
                                        >
                                            {taskPriority.charAt(0).toUpperCase() + taskPriority.slice(1)}
                                            {isCreator && (
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M4 6L8 10L12 6" stroke="white" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </span>
                                        {showPriorityDropdown && (
                                            <div className="tdm-status-dropdown-menu">
                                                {PRIORITY_OPTIONS.map(opt => (
                                                    <div
                                                        key={opt.value}
                                                        className={`tdm-status-dropdown-item ${taskPriority === opt.value ? 'active' : ''}`}
                                                        onClick={(e) => { e.stopPropagation(); handlePriorityChange(opt.value); }}
                                                    >
                                                        <span className={getPriorityBadgeClass(opt.value)}>
                                                            {opt.label}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </span>
                            </div>

                            {/* 7. Date */}
                            <div className="tdm-detail-row">
                                <span className="tdm-detail-label">
                                    <img src={calendarDotsIcon} alt="" className="tdm-row-icon" style={{width:14,height:14}} />
                                    Date
                                </span>
                                <span className="tdm-detail-value tdm-date-value">
                                    <img src={calendarDotsIcon} alt="" className="date-icon" style={{width:14,height:14}} />
                                    {formatDate(task.start_at)}
                                    {task.end_at && (
                                        <>
                                            <img src={arrowRightIcon} alt="" className="date-arrow" style={{width:14,height:14}} />
                                            <img src={calendarDotsIcon} alt="" className="date-icon" style={{width:14,height:14}} />
                                            {formatDate(task.end_at)}
                                        </>
                                    )}
                                </span>
                            </div>

                            {/* 8. Expected Days */}
                            {expectedDays !== null && expectedDays !== undefined && (
                                <div className="tdm-detail-row">
                                    <span className="tdm-detail-label">
                                        <img src={hourglassLowIcon} alt="" className="tdm-row-icon" style={{width:14,height:14}} />
                                        Expected Days
                                    </span>
                                    <span className="tdm-detail-value">{expectedDays}</span>
                                </div>
                            )}

                            {/* 9. Files Attached */}
                            <div className="tdm-detail-row tdm-files-row">
                                <span className="tdm-detail-label">
                                    <img src={fileTextIcon} alt="" className="tdm-row-icon" style={{width:14,height:14}} />
                                    Files Attached
                                </span>
                                <span className="tdm-detail-value">
                                    <button
                                        type="button"
                                        className="tdm-view-files-link"
                                        onClick={() => setShowDocumentsModal(true)}
                                    >
                                        View Files Attached
                                    </button>
                                </span>
                            </div>
                        </>)}
                        </div>

                        {/* ── Footer ── */}
                        <div className="tdm-footer">
                            <div className="tdm-footer-actions">
                                <button
                                    type="button"
                                    className="tdm-btn-close"
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="tdm-btn-toggle-complete"
                                    onClick={handleToggleComplete}
                                    disabled={isToggling}
                                >
                                    {isToggling
                                        ? 'Updating...'
                                        : isCompleted
                                            ? 'Mark as Incomplete'
                                            : 'Mark as Completed'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <CalendarTaskDocumentsModal
                isOpen={showDocumentsModal}
                onClose={() => setShowDocumentsModal(false)}
                task={task}
                attachments={rawAttachments}
                onUpload={(newAttachment) => {
                    if (fullTask) setFullTask(prev => ({
                        ...prev,
                        attachments: [newAttachment, ...(prev.attachments || [])],
                    }));
                    if (onUpdated) onUpdated({ ...task, attachments: [newAttachment, ...rawAttachments] });
                }}
            />

            <CalendarDeleteTaskModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                loading={isDeleting}
            />
        </>
    );
};

export default TaskDetailsModal;
